import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TicketTable } from '../components/TicketTable'
import { useSupportStats, useTickets } from '../hooks/useSupport'
import type { SupportTicket, TicketStatus } from '@/types/entities'
import {
  RefreshCw,
  Inbox,
  AlertCircle,
  CheckCircle,
  Clock,
  Bot,
  Headset,
  ShieldAlert,
  MessageSquare,
} from 'lucide-react'

export function SupportPage() {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all')
  const [productFilter, setProductFilter] = useState<'all' | 'memoryframe' | 'marketplace' | 'unknown'>('all')

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useSupportStats()
  const { data: ticketData, isLoading: ticketsLoading, refetch: refetchTickets } = useTickets({
    status: statusFilter === 'all' ? undefined : statusFilter,
    product: productFilter === 'all' ? undefined : productFilter,
    limit: 50,
  })

  const handleViewTicket = (ticket: SupportTicket) => {
    navigate(`/app/support/${ticket.id}`)
  }

  const handleRefresh = () => {
    refetchStats()
    refetchTickets()
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Support</h1>
          <p className="text-muted-foreground">
            Manage customer support tickets and monitor AI agent activity.
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Needs Attention"
          value={stats?.awaitingOperator}
          icon={<AlertCircle className="h-4 w-4 text-red-500" />}
          isLoading={statsLoading}
          highlight
        />
        <StatCard
          label="New Tickets"
          value={stats?.newTickets}
          icon={<Inbox className="h-4 w-4" />}
          isLoading={statsLoading}
        />
        <StatCard
          label="Awaiting Customer"
          value={stats?.awaitingCustomer}
          icon={<Clock className="h-4 w-4" />}
          isLoading={statsLoading}
        />
        <StatCard
          label="Resolved"
          value={stats?.resolvedTickets}
          icon={<CheckCircle className="h-4 w-4 text-green-500" />}
          isLoading={statsLoading}
        />
      </div>

      {/* Agent performance row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Agent Auto-Replied"
          value={stats?.agentAutoReplied}
          icon={<Bot className="h-4 w-4 text-violet-500" />}
          isLoading={statsLoading}
        />
        <StatCard
          label="Agent Escalated"
          value={stats?.agentEscalated}
          icon={<Headset className="h-4 w-4 text-blue-500" />}
          isLoading={statsLoading}
        />
        <StatCard
          label="SLA Breached"
          value={stats?.slaBreachedCount}
          icon={<ShieldAlert className="h-4 w-4 text-orange-500" />}
          isLoading={statsLoading}
        />
        <StatCard
          label="Total Tickets"
          value={stats?.totalTickets}
          icon={<MessageSquare className="h-4 w-4" />}
          isLoading={statsLoading}
        />
      </div>

      {/* Escalation Queue callout */}
      {(stats?.awaitingOperator ?? 0) > 0 && (
        <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/10 dark:border-red-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Review Queue
              <Badge variant="destructive">{stats?.awaitingOperator}</Badge>
            </CardTitle>
            <CardDescription>
              Tickets waiting for a person: escalated by the AI agent, or drafted and waiting for someone to send.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setStatusFilter('awaiting_operator')}
            >
              View tickets needing attention
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Ticket List */}
      <Card>
        <CardHeader>
          <CardTitle>Tickets</CardTitle>
          <CardDescription>
            All support conversations — click a ticket to view the full thread and agent activity.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={productFilter} onValueChange={(value) => setProductFilter(value as typeof productFilter)}>
            <SelectTrigger className="mb-4 w-[240px]" aria-label="Filter tickets by product">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              <SelectItem value="memoryframe">MemoryFrame</SelectItem>
              <SelectItem value="marketplace">Arthaus Marketplace</SelectItem>
              <SelectItem value="unknown">Product unconfirmed</SelectItem>
            </SelectContent>
          </Select>
          <Tabs
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as TicketStatus | 'all')}
          >
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="new">New</TabsTrigger>
              <TabsTrigger value="awaiting_operator">
                Needs Attention
              </TabsTrigger>
              <TabsTrigger value="awaiting_customer">Awaiting Customer</TabsTrigger>
              <TabsTrigger value="resolved">Resolved</TabsTrigger>
              <TabsTrigger value="spam">Spam</TabsTrigger>
            </TabsList>
            <TabsContent value={statusFilter} className="mt-4">
              <TicketTable
                tickets={ticketData?.data || []}
                onViewTicket={handleViewTicket}
                isLoading={ticketsLoading}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

// ─── Stat Card ──────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
  isLoading,
  highlight,
}: {
  label: string
  value?: number
  icon: React.ReactNode
  isLoading?: boolean
  highlight?: boolean
}) {
  return (
    <Card className={highlight && (value ?? 0) > 0 ? 'border-red-200 dark:border-red-900' : ''}>
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-2">
          {icon}
          {label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-7 w-12" />
        ) : (
          <p className={`text-2xl font-bold ${highlight && (value ?? 0) > 0 ? 'text-red-600' : ''}`}>
            {value ?? 0}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
